/**
 * Prize display component
 * Shows the current prize being drawn with epic animations
 * 
 * Note: prizeNumber and totalPrizes props are deprecated.
 * Progress is now shown via a separate progressBadge in parent components.
 */

import { useMemo, memo } from "react";
import type { PrizeInfo } from "@/lib/websocket";
import styles from "./PrizeDisplay.module.css";
import "../raffle-theme.css";

// =============================================================================
// Types
// =============================================================================

interface PrizeDisplayProps {
  prize: PrizeInfo | null;
  isDrawing?: boolean;
  isWon?: boolean;
  /** @deprecated Progress is now shown via progressBadge in parent components */
  prizeNumber?: number;
  /** @deprecated Progress is now shown via progressBadge in parent components */
  totalPrizes?: number;
  size?: "normal" | "large";
}

// =============================================================================
// Component
// =============================================================================

export const PrizeDisplay = memo(function PrizeDisplay({
  prize,
  isDrawing = false,
  isWon = false,
  // Deprecated props - kept for backwards compatibility but ignored
  prizeNumber: _prizeNumber,
  totalPrizes: _totalPrizes,
  size = "normal",
}: PrizeDisplayProps) {
  // Generate placeholder gradient if no image
  const placeholderStyle = useMemo(() => {
    if (prize?.imageUrl) return undefined;

    const colors = [
      ["#667eea", "#764ba2"],
      ["#f093fb", "#f5576c"],
      ["#4facfe", "#00f2fe"],
      ["#43e97b", "#38f9d7"],
      ["#fa709a", "#fee140"],
      ["#a18cd1", "#fbc2eb"],
    ];

    const hash = prize?.name ? prize.name.length % colors.length : 0;
    const [from, to] = colors[hash];

    return {
      background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
    };
  }, [prize?.imageUrl, prize?.name]);

  if (!prize) {
    return (
      <div className={`${styles.container} ${styles[size]} ${styles.empty}`}>
        {/* SVG gift icon instead of emoji */}
        <div className={styles.emptyIconSvg}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="10" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <rect x="1" y="7" width="22" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 7V21" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 7C12 7 12 4 9 4C6 4 6 7 9 7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M12 7C12 7 12 4 15 4C18 4 18 7 15 7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        </div>
        <div className={styles.emptyText}>Приз не выбран</div>
      </div>
    );
  }

  return (
    <div
      className={`
        ${styles.container} 
        ${styles[size]} 
        ${isDrawing ? styles.drawing : ""} 
        ${isWon ? styles.won : ""}
      `}
    >
      {/* Gradient border effect when drawing */}
      {isDrawing && <div className={styles.gradientBorder} />}

      {/* Prize image or placeholder */}
      <div className={styles.imageContainer}>
        {/* Glow effect when drawing - rendered first so it's behind */}
        {isDrawing && <div className={styles.glowEffect} />}
        
        {prize.imageUrl ? (
          <img
            src={prize.imageUrl}
            alt={prize.name}
            className={styles.image}
            loading="eager"
          />
        ) : (
          <div className={styles.placeholder} style={placeholderStyle}>
            {/* SVG gift icon instead of emoji */}
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.placeholderIconSvg}>
              <rect x="3" y="10" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" fill="rgba(255,255,255,0.1)"/>
              <rect x="1" y="7" width="22" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="rgba(255,255,255,0.15)"/>
              <path d="M12 7V21" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 7C12 7 12 4 9 4C6 4 6 7 9 7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <path d="M12 7C12 7 12 4 15 4C18 4 18 7 15 7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>
        )}

        {/* Sparkle effect when won */}
        {isWon && (
          <>
            <div className={styles.sparkle} style={{ "--delay": "0s" } as React.CSSProperties} />
            <div className={styles.sparkle} style={{ "--delay": "0.15s" } as React.CSSProperties} />
            <div className={styles.sparkle} style={{ "--delay": "0.3s" } as React.CSSProperties} />
            <div className={styles.sparkle} style={{ "--delay": "0.45s" } as React.CSSProperties} />
          </>
        )}
      </div>

      {/* Prize info */}
      <div className={styles.info}>
        <h2 className={styles.name}>{prize.name}</h2>
        {prize.description && (
          <p className={styles.description}>{prize.description}</p>
        )}
      </div>

      {/* Winner badge - with trophy SVG icon */}
      {isWon && (
        <div className={styles.winnerBadge}>
          <span className={styles.winnerIconSvg}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 15C8.68629 15 6 12.3137 6 9V3H18V9C18 12.3137 15.3137 15 12 15Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6 5H3V7C3 8.65685 4.34315 10 6 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M18 5H21V7C21 8.65685 19.6569 10 18 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M8 21H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M12 15V18M12 18H8V21H16V18H12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <span className={styles.winnerText}>ВЫИГРАНО!</span>
        </div>
      )}
    </div>
  );
});

export default PrizeDisplay;
